<template>
  <div class="chat-container">
    <div class="chat-header">
      {{ $t('chat.title') }}
    </div>
    <div class="chat-messages" ref="messageContainer">
      <template v-if="isLoadingOldMessage">
        <div class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </template>
      <template v-if="!isLoading">
        <template v-if="groupedMessages">
          <div v-for="(group, groupIndex) in groupedMessages" :key="groupIndex" class="message-group">
            <div class="date-header md-font text-center">
              {{ $moment(groupIndex).format('DD/MM/YYYY') }}
            </div>
            <div class="pb-2" v-for="(message, messageIndex) in Object.values(group)"
              :key="`${groupIndex}-${messageIndex}`">
              <div v-if="messageIndex == 0" class="date-header text-center">
                {{ $moment(message[0].createdAt).format('HH:mm') }}
              </div>
              <div class="message-list" v-for="(v, index) in message" :key="`${groupIndex}-${messageIndex}-${index}`">
                <div :class="['message-wrapper', v.admin || v.employee ? 'own-message' : 'other-message']">
                  <div class="message-avatar" :class="{ 'invisible': !checkIsFirstMessage(v, index) }">
                    <v-avatar size="40" v-if="v.admin || v.employee" color="#fff">
                      <v-img :src="market?.companyData?.logo?.url ? market?.companyData?.logo?.url : '/user.png'" contain></v-img>
                    </v-avatar>
                    <v-avatar size="40" v-else color="#fff">
                      <v-img
                        :src="v.user?.profileImage?.url ? v.user.profileImage.url : '/user.png'"
                        :alt="v.sender"></v-img>
                    </v-avatar>
                  </div>
                  <div class="message-content">
                    <div v-if="checkIsFirstMessage(v, index)" class="message-name">
                      {{ getMessageName(v) }}
                      <div v-if="!v.admin && !v.employee" class="message-chip">
                        {{ $t('chat.customer') }}
                      </div>
                      <!-- <div v-else-if="v.admin || v.employee" class="message-chip">
                        {{ $t('chat.employee') }}
                      </div> -->
                    </div>
                    <template v-if="v.text">
                      <div class="message-bubble">
                        {{ v.text }}
                      </div>
                    </template>
                    <template v-if="v.images">
                      <a v-for="(image, index) in v.images" :key="index" :href="image.url" target="_blank" :class="{ 'image-bubble': checkIsImage(image.url) }" class="message-bubble">
                        <template v-if="checkIsImage(image.url)">
                          <img :src="image.url" alt="image" />
                        </template>
                        <template v-else>
                          <u>{{ $t('chat.file_attached') }}</u>
                        </template>
                      </a>
                    </template>
                  </div>
                </div>
                <div class="message-footer-wrapper" :class="{ 'own-footer': v.admin || v.employee }">
                  <div class="message-footer">
                    <div class="message-meta">
                      <template v-if="v.admin || v.employee">
                        <template v-if="shouldShowMessageInfo(v, groupIndex, messageIndex, index)">
                          <div class="message-by">{{ $t('chat.by') }} {{ getSendBy(v) }}</div>
                          <span class="message-status">
                            <v-icon v-if="checkIsRead(v)" small color="success">mdi-check-all</v-icon>
                            <v-icon v-else small color="success">mdi-check</v-icon>
                          </span>
                        </template>
                      </template>
                      <template v-if="shouldShowMessageInfo(v, groupIndex, messageIndex, index)">
                        <span class="message-time">{{ $moment(v.createdAt).format('HH:mm') }} {{
                          $t('timeunit.minute.short') }}</span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
      <template v-else>
        <div class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </template>
    </div>

    <template v-if="!disabled">
      <div class="chat-input">
        <div class="input-wrapper">
          <v-btn icon @click="handleAttachment" :disabled="isLoading">
            <img src="@/assets/icons/gallery.svg" alt="gallery" />
          </v-btn>
          <v-text-field v-model="newMessage" :placeholder="$t('chat.send_message') + '...'"
            @keydown.enter.stop.prevent="createMessage" underlined hide-details class="message-input"
            :disabled="isLoadingCreateMsg" ref="messageInput"></v-text-field>
          <v-btn icon @click="createMessage" :disabled="!newMessage.trim() || isLoadingCreateMsg">
            <img src="@/assets/icons/send.svg" alt="send" />
          </v-btn>
        </div>
      </div>
      <input type="file" ref="fileInput" accept="image/*" style="display: none" @change="onFileSelected">
    </template>
    <template v-else>
      <div class="chat-input py-4 text-center">
        <div class="input-wrapper text-center justify-center">
          <h5 v-if="appeal.status === 'success'">{{ $t('chat.no_message_to_send_finish_appeal') }}</h5>
          <h5 v-else-if="appeal.status === 'cancelled'">{{ $t('chat.no_message_to_send_cancel_appeal') }}</h5>
          <h5 v-else>{{ $t('chat.no_message_to_send') }}</h5>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { FileUpload } from "../classes";

export default {
  name: 'ChatComponent',
  props: {
    projectId: {
      type: String,
    },
    marketId: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    appeal: {
      type: Object,
    },
    limit: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      chat: null,
      messages: [],
      newMessage: '',
      selectedFile: null,
      isLoading: false,
      isNewMessage: false,
      pollingInterval: null,
      infiniteLoading: false,
      startDate: this.$moment().toDate(),
      endDate: null,
      loadMore: false,
      isLoadingCreateMsg: false,
      isLoadingOldMessage: false,
      maxFileUpload: 5242880,
      isMaxFileUpload: false,
    }
  },
  computed: {
    ...mapGetters({
      market: 'markets/getMarket',
      getMarketName: 'markets/getMarketName'
      
    }),
    admin() {
      return this.$store.getters["admin/getByMarketId"](this.marketId);
    },
    employee() {
      return this.$store.getters["employee/getByMarketId"](this.marketId);
    },
    groupedMessages() {
      const groups = {};
      if(this.messages){
        const sortedMessages = [...this.messages].sort((a, b) => 
          new Date(a.createdAt) - new Date(b.createdAt)
        );
        let groupCounter = 0;
        let lastUserId = null;
        let lastDate = null;
        sortedMessages.forEach((message, index) => { 
          const date = this.$moment(message.createdAt).format('YYYY-MM-DD');
          const time = this.$moment(message.createdAt).format('HH:mm');
          const isStaff = message.admin || message.employee;
          const userId = isStaff 
            ? (message.user?.objectId || message.admin?.objectId || message.employee?.objectId)
            : message.customerUser?.objectId;
          if (date !== lastDate || userId !== lastUserId) {
            groupCounter++;
            lastUserId = userId;
            lastDate = date;
          }
          const timeUserKey = `${time}-${userId}-${groupCounter}`;
          if (!groups[date]) {
            groups[date] = {};
          }
          if (!groups[date][timeUserKey]) {
            groups[date][timeUserKey] = [];
          }
          groups[date][timeUserKey].push(message);
        });
      } 
      return groups;
    },
  },
  created() {
    this.initData();
  },
  methods: {
    ...mapMutations({
      setReadChat: "appeal/setReadChat",
      setIncrementMessageCount: "appeal/setIncrementMessageCount",
      setUpdateMessagesCount: "appeal/setUpdateMessagesCount",
    }),
    async initData() {
      try {
        this.messages = [];
        this.chat = null;
        this.endDate = null;

        if (this.appeal && this.appeal.objectId && this.appeal.chat) {
          this.chat = this.appeal.chat;
          await this.getMessages(this.chat.objectId);
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          this.startPolling();
        } else if (this.appeal && this.appeal.objectId) {
          const appeal = await this.getAppeal();
          if (appeal.chat) {
            this.chat = appeal.chat;
            await this.getMessages(this.chat.objectId);
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
          this.startPolling();
        }
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(error),
          color: "error"
        });
      }
    },
    async getAppeal() {
      const appeal = await this.$store.dispatch('appeal/getAppeal', { id: this.appeal.objectId });
      return appeal;
    },
    startPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
      this.pollingInterval = setInterval(() => {
        this.loadNewMessages();
      }, 10000);
    },
    async loadNewMessages(loadMore = false) {
      if (!this.chat) {
        await this.initData();
        return;
      }
      this.isNewMessage = true;
      if (loadMore) {
        this.loadMore = true;
      }
      try {
        if (this.messages && this.messages.length > 0) {
          this.endDate = this.$moment(this.getLastMessage().createdAt).toDate();
        }
        const response = await this.$store.dispatch('message/getMessages', {
          chatId: this.chat.objectId,
          limit: this.limit,
          endDate: this.endDate
        });
        if (response && response.length > 0) {
          const customerUserMsg = response.find(msg => msg.customerUser);
          if (customerUserMsg) {
            this.messages = this.messages.map(msg => ({
              ...msg,
              readUsers: [customerUserMsg.customerUser]
            }));
          }
          this.messages = [...this.messages, ...response];
          if (this.appeal) {
            this.setUpdateMessagesCount({ appeal: this.appeal, message: response });
          }
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
        this.loadMore = false;
        this.isNewMessage = false;
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(error),
          color: "error"
        });
      }
    },
    async loadOldMessages() {
      if (this.infiniteLoading || !this.chat) return;
      this.infiniteLoading = true;
      try {
        this.isLoadingOldMessage = true;
        const container = this.$refs.messageContainer;
        const oldScrollHeight = container.scrollHeight;
        const oldScrollTop = container.scrollTop;

        if (this.messages && this.messages.length > 0) {
          this.startDate = this.$moment(this.getFirstMessage().createdAt).toDate();
        }
        const response = await this.$store.dispatch('message/getMessages', {
          chatId: this.chat.objectId,
          startDate: this.startDate,
          limit: this.limit
        });
        if (response && response.length > 0) {
          this.messages = [...response, ...this.messages];
          this.$nextTick(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDiff = newScrollHeight - oldScrollHeight;
            container.scrollTop = oldScrollTop + heightDiff;
          });
        }
        this.isLoadingOldMessage = false;
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(error),
          color: "error"
        });
      } finally {
        this.infiniteLoading = false;
      }
    },
    async createChat() {
      this.isLoading = true;
      try {
        let response = null;
        if (this.appeal) {
          response = await this.$store.dispatch('chat/createChat', { appealId: this.appeal.objectId });
          this.chat = response.appeal.chat;
        }
        this.isLoading = false;
        this.startPolling();
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(error),
          color: "error"
        });
      }
    },
    async getMessages(id) {
      this.isLoading = true;
      try {
        const messages = await this.$store.dispatch('message/getMessages', {
          chatId: id,
          limit: this.limit,
          startDate: this.startDate,
          endDate: this.endDate
        });
        if (messages) {
          this.messages = [...this.messages, ...messages];
        } else {
          this.messages = messages || [];
        }
        this.isLoading = false;
        if (this.appeal) {
          this.setReadChat({ appeal: this.appeal });
        }
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(error),
          color: "error"
        });
        this.isLoading = false;
      }
    },
    async createMessage() {
      this.isLoadingCreateMsg = true;
      try {
        if (!this.newMessage.trim()) {
          this.isLoadingCreateMsg = false;
          this.$nextTick(() => {
            this.$refs.messageInput.focus();
          });
          return;
        }
        if (this.chat || (this.appeal && this.appeal.chat)) {
          this.chat = this.appeal.chat || this.chat;
        } else {
          await this.createChat();
        }
        if (this.chat) {
          let message = await this.$store.dispatch('message/createMessage', {
            chatId: this.chat.objectId,
            text: this.newMessage,
            adminId: this.admin?.objectId,
            employeeId: this.employee?.objectId
          });
          if (this.messages && this.messages.length > 0) {
            this.messages = [...this.messages, message];
          } else {
            this.messages = [message];
          }
          if (this.appeal) {
            this.setIncrementMessageCount({ appeal: this.appeal });
          }
          this.newMessage = '';
          this.isLoadingCreateMsg = false;

          this.$nextTick(() => {
            this.scrollToBottom();
            this.$refs.messageInput.focus();
          });
        }
      } catch (error) {
        console.log(error);
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(error),
          color: "error"
        });
        this.isLoading = false;
      }
    },
    handleAttachment() {
      this.$refs.fileInput.click();
    },
    async onFileSelected(event) {
      const file = event.target.files[0];
      if (file.size <= this.maxFileUpload) {
        let uploaded = await FileUpload.upload(file);
        if (!file) return;
          if(this.chat || (this.appeal && this.appeal.chat)){
            this.chat = this.appeal.chat || this.chat;
          }else{
            await this.createChat();
          }
        if(this.chat){
          let message = await this.$store.dispatch('message/createMessage', {
            chatId: this.chat.objectId,
            images: [uploaded],
            adminId: this.admin?.objectId,
            employeeId: this.employee?.objectId
          });
          if(this.messages && this.messages.length > 0){
            this.messages = [...this.messages, message];
          }else{
            this.messages = [message];
          }
          if(this.appeal){
            this.setIncrementMessageCount({ appeal: this.appeal });
          }
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          this.$refs.messageInput.focus();
        }
        event.target.value = '';
        this.isMaxFileUpload = false;
      }else{
        this.isMaxFileUpload = true;
        event.target.value = '';
        this.$toast.show({
          content: this.$helper.ShowToastCatchError(this.$t('chat.max_file_upload_error', { size: 5 })),
          color: "error"
        });
      }
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    handleScroll(event) {
      const container = event.target;
      if (container.scrollTop === 0 && !this.infiniteLoading) {
        this.loadOldMessages();
      }
    },
    getFullName(user) {
      if (user && user.firstName && user.lastName) {
        return user.firstName + ' ' + user.lastName;
      } else if (user && user.firstName) {
        return user.firstName;
      } else if (user && user.lastName) {
        return user.lastName;
      } else if (user && user.username) {
        return user.username;
      } else {
        return '-';
      }
    },
    getSendBy(message) {
      if (message.admin && message.admin.user) {
        return this.getFullName(message.admin.user);
      } else if (message.employee && message.employee.user) {
        return this.getFullName(message.employee.user);
      }
    },
    getMessageName(message) {
      if (message.admin || message.employee) {
        return this.getMarketName;
      } else {
        if (message.user) {
          return message.user.firstName + ' ' + message.user.lastName;
        } else {
          return '-';
        }
      }
    },
    checkIsRead(message) {
      if (message.readUsers && message.readUsers.length > 0) {
        const currentUserId = message.admin?.user?.objectId || message.employee?.user?.objectId;
        return message.readUsers.some(user => user.objectId != currentUserId);
      }
      return false;
    },
    getFirstMessage() {
      if(this.messages && this.messages.length > 0) {
        const sortedMessages = [...this.messages].sort((a, b) => 
          new Date(a.createdAt) - new Date(b.createdAt)
        );
        return sortedMessages[0];
      }
      return null;
    },
    getLastMessage() {
      if(this.messages && this.messages.length > 0) {
        const sortedMessages = [...this.messages].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        return sortedMessages[0];
      }
      return null;
    },
    checkIsFirstMessage(message, index) {
      if (!message) return false;
      const currentDate = this.$moment(message.createdAt).format('YYYY-MM-DD');
      const dateGroups = this.groupedMessages[currentDate];
      if (!dateGroups) return false;
      for (const [key, messages] of Object.entries(dateGroups)) {
        const messageIndex = messages.findIndex(m => m === message);
        if (messageIndex !== -1) {
          return messageIndex === 0;
        }
      }
      return false;
    },
    shouldShowMessageInfo(message, groupIndex, messageIndex, index) {
      if (!this.groupedMessages) return false;
      const group = this.groupedMessages[groupIndex];
      const timeGroup = Object.values(group)[messageIndex];
      return index === timeGroup.length - 1;
    },
    checkIsImage(value){
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const extension = value.split('.').pop().toLowerCase();
      return imageExtensions.includes(extension);
    }
  },
  mounted() {
    const container = this.$refs.messageContainer;
    if (container) {
      this.scrollToBottom();
      setTimeout(() => {
        container.addEventListener('scroll', this.handleScroll);
      }, 1000);
    }
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(700px - 150px);
  min-height: calc(700px - 150px);
  background-color: white;
  border-radius: 5px;
  border: 1px solid #DADADA;
  overflow: hidden;
}

.chat-header {
  border-bottom: 1px solid #DADADA;
  padding: 14px 16px;
  font-size: 18px;
  font-weight: 500;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #f8f9fa;
}

.message-group {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.message-wrapper {
  display: flex;
  gap: 15px;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }

  &.own-message {
    flex-direction: row-reverse;

    .message-content {
      align-items: flex-end;
    }

    .message-bubble {
      background-color: #216B5B;
      color: white;
      position: relative;

      &:not(:first-child) {
        border-top-right-radius: 16px;
      }

      &.image-bubble {
        background-color: transparent;
        padding: 0;
        border-radius: 16px;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }

  &.other-message {
    .message-bubble {
      background-color: #6E6E6E;
      border-radius: 12px 12px 12px 12px;
      color: white;
      position: relative;

      &:not(:first-child) {
        border-top-left-radius: 16px;
      }

      &.image-bubble {
        background-color: transparent;
        padding: 0;
        border-radius: 16px;
      }
    }
  }
}

.message-list {
  padding-bottom: 5px;

  .own-message {
    .message-bubble {
      border-radius: 16px 5px 5px 16px;
      overflow-wrap: anywhere;
    }
  }

  .other-message {
    .message-bubble {
      border-radius: 5px 16px 16px 5px;
      overflow-wrap: anywhere;
    }
  }
}

.message-list:first-child {
  .own-message {
    .message-bubble {
      border-top-right-radius: 16px;
    }
  }

  .other-message {
    .message-bubble {
      border-top-left-radius: 16px;
    }
  }
}

.message-list:last-child {
  .own-message {
    .message-bubble {
      border-bottom-right-radius: 16px;
    }
  }

  .other-message {
    .message-bubble {
      border-bottom-left-radius: 16px;
    }
  }
}

.message-avatar {
  flex-shrink: 0;
  width: 40px;
  z-index: 1;

  &.invisible {
    visibility: hidden;
    height: 0;
  }
}

.message-content {
  display: flex;
  flex-direction: column;
  max-width: 70%;
  align-items: self-start;
}

.message-name {
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;

  .message-chip {
    background-color: #E6F4F0;
    color: #216B5B;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
  }
}

.message-bubble {
  padding: 5px 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-bottom: 2px;

  &.image-bubble {
    max-width: 400px;
    overflow: hidden;
    border-radius: 8px;
    box-shadow: none;
  }
}

.message-footer-wrapper {
  display: flex;
  justify-content: flex-start;

  &.own-footer {
    justify-content: flex-end;

    .message-meta {
      text-align: right;

      .message-status {
        margin-right: 5px;
      }
    }
  }
}

.message-footer {
  padding: 0 48px;
}

.message-meta {
  // display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #8A9099;
  line-height: 1.5;
}

.message-by {
  font-weight: 500;
}

.chat-input {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding: 5px 8px 8px 8px;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  .message-input {
    margin: 0 !important;
    padding: 0 !important;

    ::v-deep .v-input__control {
      min-height: 44px;

      .v-input__slot {
        margin-bottom: 0;
        background-color: #fff !important;
      }
    }
  }
}

.date-header {
  font-size: 13px;
  color: #6E6E6E;
  font-weight: 500;

  &.md-font {
    font-size: 16px;
  }
}

.send-icon {
  color: #216B5B;
  transform: rotate(45deg);
}
</style>